// Hideo - theme and main colour applied to the page: <html class="dark"> and the CSS variables of the main colour
// (--brand, --brand-dark, --r950, --r900, --thumb, --on-brand, --brand-text; docs/architecture.md › Design system).
// On top of them the app theme (src/features/appThemes.ts): Default leaves everything as above; any other theme puts
// <html data-style> and its classes and decides light or dark itself. A theme that follows the main colour (adaptive)
// gets every token written on <html>, built from the colour; a theme with its own colours takes them from
// src/styles/theme-tokens.css (the inline tokens are then removed, so the stylesheet's win).
// One main colour (creatorOS_accent) for Default and every adaptive theme. Old stored theme keys (before 4/11/2026) keep
// their own colour until the user picks a theme or a colour (creatorOS_accentShared = '1' from then on: adopt()).
// "Follow my device" = creatorOS_theme 'auto' for every theme: the theme or its twin in the other tab, as the system asks.
// Ported from the legacy core/theme.js; the colour maths is src/features/theme.ts. Chosen only in Settings › Appearance
// (SettingsPage.vue, StylePicker.vue). The anti-flash script in index.html's <head> applies the stored choice before
// anything is drawn: Default with the same maths as accentVars, the other themes from what this store last applied
// (creatorOS_styleCss, written by paint). init (called by onReady at DOMContentLoaded, src/features/boot.ts) applies it
// again with everything derived and starts following the system theme. Like before, the preference is read from
// localStorage at every call and the colour is not reactive: the Vue pages redraw on refreshVue (renderAll), so the
// native colour picker stays open while the colour changes.
// The background layer (src/features/backdrop.ts: pattern, glass panels, grain; creatorOS_backdrop) is applied here too,
// after each theme change, because what it may show depends on the theme.
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { fixContrast } from '../features/editor/colors'
import { ACCENT_KEY, DEF_ACCENT, THEME_KEY, accentVars, isDarkTheme, resolveTheme, validHex } from '../features/theme'
import { DEFAULT_STYLE, TOKEN_NAMES, applyStyleToHtml, impliedAccent, parseStyle, readStyleRaw, shownStyle, storeStyle, styleClasses,
  styleDef, styleTokens, tokenCss, type StyleGroup } from '../features/appThemes'
import { applyBackdropToHtml, readBackdrop, storeBackdrop, type Backdrop } from '../features/backdrop'
import { useUiStore } from './ui'
import { app } from '../features/app'

/** The main colour's variables set on <html> with Default */
const ACCENT_VARS = ['--brand', '--brand-dark', '--r950', '--r900', '--thumb', '--on-brand', '--brand-text']
/** '1' once the user picked a theme or a colour with the one-colour model: the main colour is creatorOS_accent for every theme */
export const SHARED_KEY = 'creatorOS_accentShared'
/** What the store last applied for a theme other than Default, for the anti-flash script (JSON, see paint) */
export const STYLE_CSS_KEY = 'creatorOS_styleCss'
/** Milliseconds of the cross-fade between two themes (src/styles/themes.css, .theme-switching) */
const SWITCH_MS = 900

/** The system's dark-theme query (null without matchMedia) */
const sysDark = (): MediaQueryList | null => (typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : null)
const lsGet = (k: string): string | null => { try { return localStorage.getItem(k) } catch { return null } }
const lsSet = (k: string, v: string | null): void => {
  try { if (v === null) localStorage.removeItem(k); else localStorage.setItem(k, v) } catch { /* storage full or blocked */ }
}

export const useThemeStore = defineStore('theme', () => {
  /** The user's main colour (#rrggbb, creatorOS_accent) */
  let accent = DEF_ACCENT
  let listening = false
  /** The app theme shown ('default' = Hideo's own look); reactive for the picker and the speaker button */
  const style = ref<string>(DEFAULT_STYLE)
  /** The theme chosen (the stored one, old keys read as their new theme); with "Follow my device" `style` may be its twin */
  const choice = ref<string>(DEFAULT_STYLE)
  /** The background layer chosen on this device (creatorOS_backdrop); reactive for its picker */
  const backdrop = ref<Backdrop>(readBackdrop())
  let switchTimer: ReturnType<typeof setTimeout> | undefined
  /** Hooks of the background and the sounds (src/features/sky.ts, src/stores/sounds.ts), set by installSky / installSounds;
   *  repaint: the main colour of the theme shown changed */
  const hooks: { beforeSwitch?: () => void; afterSwitch?: (key: string, animate: boolean) => void; repaint?: (key: string) => void } = {}

  /** Theme preference: 'light' | 'dark' | 'auto' (missing = dark) */
  function mode(): string { return lsGet(THEME_KEY) || 'dark' }
  const resolve = (m: string) => { const q = sysDark(); return resolveTheme(m, q ? q.matches : null) }
  const systemDark = () => resolve('auto') === 'dark'
  /** The user's main colour (the Default one and, once shared, every adaptive theme's) */
  const curAccent = () => accent
  /** The one-colour model is in use (see adopt) */
  const shared = () => lsGet(SHARED_KEY) === '1'
  /** "Follow my device" applies: Default always; another theme once the user has used the new picker (an old stored theme
   *  stays exactly as it was until then) */
  const follows = () => mode() === 'auto' && (choice.value === DEFAULT_STYLE || shared())
  /** The main colour of the theme shown: Default and adaptive themes the user's (an old stored key: its own until adopt);
   *  a theme with its own colours its own */
  function themeAccent(): string {
    const s = styleDef(style.value)
    return !s ? accent : s.adaptive ? sharedAccent() : s.palette.accent
  }
  /** The colour of the themes that follow it: the user's, or an old stored theme's own until adopt */
  const sharedAccent = (): string => (shared() ? null : impliedAccent(readStyleRaw())) ?? accent
  /** The theme to show now (the choice, or its twin with "Follow my device") */
  const shownKey = () => shownStyle(choice.value, follows(), systemDark())

  /** Writes the theme's colours on <html>: Default the main colour's variables (as always), an adaptive theme every token
   *  of its palette for the colour, a fixed theme nothing (the stylesheet's). Then the anti-flash cache */
  function paint(): void {
    const html = document.documentElement, st = html.style, s = styleDef(style.value)
    if (!s) {
      for (const k of TOKEN_NAMES) if (!ACCENT_VARS.includes(k)) st.removeProperty(k)
      for (const [k, v] of accentVars(accent, isDarkTheme())) st.setProperty(k, v)
    } else if (s.adaptive) {
      for (const [k, v] of styleTokens(s.key, themeAccent())) st.setProperty(k, v)
    } else for (const k of TOKEN_NAMES) st.removeProperty(k)
    html.removeAttribute('data-from')
    writeCache()
  }
  /** creatorOS_styleCss: { k: stored value, f: follows the device, n: 'd' | 'l' (shown), d / l: { s: key, c: <html> classes,
   *  v: inline tokens } of the pair } — what the anti-flash script needs to show the same thing at the next start */
  function writeCache(): void {
    const raw = readStyleRaw(), cur = styleDef(style.value)
    if (!cur || !raw) { lsSet(STYLE_CSS_KEY, null); return }
    const a = themeAccent(), twin = styleDef(cur.twin)!
    const entry = (s: typeof cur) => ({ s: s.key, c: [...styleClasses(s.key), ...(s.dark ? ['dark'] : [])].join(' '), v: s.adaptive ? tokenCss(styleTokens(s.key, a)) : '' })
    const [d, l] = cur.group === 'dark' ? [cur, twin] : [twin, cur]
    lsSet(STYLE_CSS_KEY, JSON.stringify({ k: raw, f: follows() ? 1 : 0, n: cur.group === 'dark' ? 'd' : 'l', d: entry(d), l: entry(l) }))
  }

  /** Applies a main colour (none, or anything that is not #rrggbb = the default) to the CSS variables of the current theme */
  function applyAccent(h?: string | null): void {
    accent = validHex(h) ? h : DEF_ACCENT
    paint()
  }
  /** The phone's browser bar and the installed app's title bar (meta theme-color) take the theme's background (--bg) */
  function syncBarColor(): void {
    const meta = document.querySelector('meta[name="theme-color"]')
    const bg = getComputedStyle(document.documentElement).getPropertyValue('--bg').trim()
    if (meta && bg) meta.setAttribute('content', `rgb(${bg.split(/\s+/).join(',')})`)
  }
  /** Shows the theme to show now (the choice or its twin), light or dark: with Default `theme` ('light' / 'dark') decides.
   *  The colours are derived again, the pages redrawn (tag and format colours and coloured script text adapt to the new
   *  background). A theme change cross-fades the background when animate (class theme-switching for SWITCH_MS) */
  function show(theme: string, animate: boolean): void {
    const html = document.documentElement, key = shownKey(), changed = key !== style.value || html.getAttribute('data-style') !== (styleDef(key) ? key : null)
    if (changed) {
      if (animate) {
        hooks.beforeSwitch?.()
        html.classList.add('theme-switching')
        clearTimeout(switchTimer)
        switchTimer = setTimeout(() => html.classList.remove('theme-switching'), SWITCH_MS)
      }
      style.value = key
      applyStyleToHtml(html, key)
    }
    applyBackdropToHtml(html, key, backdrop.value)
    const s = styleDef(key)
    html.classList.toggle('dark', s ? s.dark : theme === 'dark')
    paint()
    syncBarColor()
    if (app.booted) app.renderAll()
    fixContrast(document.getElementById('scriptBody'))
    if (changed) hooks.afterSwitch?.(key, animate)
  }
  /** Shows a theme ('light' / 'dark', the user's preference resolved) */
  function applyTheme(theme: string): void { show(theme, false) }

  /** The first choice made with the one-colour model (a theme, the colour or "Follow my device"): from now on
   *  creatorOS_accent is the colour of every adaptive theme. Picking a theme from an old one (Default too) carries its
   *  colour over: the colour the picker's Main colour row showed */
  function adopt(carryColour: boolean): void {
    if (shared()) return
    lsSet(SHARED_KEY, '1')
    const imp = impliedAccent(readStyleRaw())
    if (carryColour && imp) { accent = imp; lsSet(ACCENT_KEY, imp) }
  }
  /** Stores and applies the theme preference */
  function setMode(m: string): void {
    lsSet(THEME_KEY, m)
    show(resolve(m), choice.value !== DEFAULT_STYLE); useUiStore().refresh()
  }
  /** Stores and applies the main colour (the colour wins over an old theme's own) */
  function setAccent(h: string): void {
    adopt(false)
    lsSet(ACCENT_KEY, h)
    applyAccent(h)
    hooks.repaint?.(style.value)
  }
  /** "Follow my device" on or off. Off keeps what is on screen (its tab becomes the choice) */
  function setFollow(on: boolean): void {
    adopt(true)
    if (on) { setMode('auto'); return }
    const s = styleDef(style.value)
    if (s && s.key !== choice.value) { choice.value = s.key; storeStyle(s.key) }
    setMode(s ? s.group : resolve('auto'))
  }

  /** Shows an app theme and stores it ('default' = Hideo's own look). tab: the picker tab it was picked in (Default is in
   *  both: without "Follow my device" it sets light or dark). Only the background cross-fades; the panels change colour
   *  gently */
  function setStyle(key: string, animate = true, tab?: StyleGroup): void {
    key = parseStyle(key)
    adopt(true)
    if (key === DEFAULT_STYLE && tab && mode() !== 'auto') lsSet(THEME_KEY, tab)
    storeStyle(key)
    choice.value = key
    show(resolve(mode()), animate)
    useUiStore().refresh()
  }
  const curStyle = () => style.value
  /** Stores and applies a change of the background layer (what the theme shown locks is kept but not applied) */
  function setBackdrop(patch: Partial<Backdrop>): void {
    backdrop.value = { ...backdrop.value, ...patch }
    storeBackdrop(backdrop.value)
    applyBackdropToHtml(document.documentElement, style.value, backdrop.value)
    useUiStore().refresh()
  }
  /** The user's own light / dark (their preference, whatever the app theme) */
  const userDark = () => resolve(mode()) === 'dark'

  /** At start-up (onReady at DOMContentLoaded, src/features/boot.ts): the stored colour and theme, then the listeners (once) */
  function init(): void {
    const a = lsGet(ACCENT_KEY)
    accent = validHex(a) ? a : DEF_ACCENT
    choice.value = parseStyle(readStyleRaw())
    backdrop.value = readBackdrop()
    style.value = DEFAULT_STYLE
    const key = shownKey()
    if (key !== DEFAULT_STYLE || document.documentElement.hasAttribute('data-style')) { style.value = key; applyStyleToHtml(document.documentElement, key) }
    applyTheme(resolve(mode()))
    if (listening) return
    listening = true
    // "Follow my device" follows the system theme when it changes
    const q = sysDark()
    if (q && q.addEventListener) q.addEventListener('change', () => { if (mode() === 'auto') show(resolve('auto'), choice.value !== DEFAULT_STYLE) })
    // Settings: the colour label is updated once the choice is made (no redraw while picking, so the picker stays open)
    document.addEventListener('change', e => {
      const el = e.target as HTMLInputElement | null, body = document.getElementById('settingsBody')
      if (el && el.type === 'color' && body && body.contains(el)) useUiStore().refresh()
    })
  }

  return { mode, curAccent, themeAccent, sharedAccent, follows, applyAccent, applyTheme, setMode, setAccent, setFollow, init, style, choice, curStyle, setStyle, userDark, backdrop, setBackdrop, hooks }
})
